import { useState, useEffect, useCallback } from 'react';
import { fetchNotificationConfig, updateNotificationConfig } from '@/lib/api';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Mail, MessageSquare, Clock, Sparkles, FileText, Save } from 'lucide-react';

export default function NotificacionesConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setConfig(await fetchNotificationConfig()); }
    catch { toast.error('Error al cargar configuración'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateNotificationConfig(config);
      setConfig(updated);
      toast.success('Configuración guardada');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="space-y-6"><Skeleton className="h-10 w-64" />{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
  );

  if (!config) return null;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="notif-config-page">
      <div className="flex items-center justify-between">
        <PageHeader title="Configuración de Notificaciones" subtitle="Parámetros de correo, WhatsApp, reglas e IA" />
        <Button onClick={handleSave} disabled={saving} data-testid="save-config">
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </div>

      {/* ── EMAIL ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-blue-500" /> Correo Electrónico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Envío por correo habilitado</Label>
            <Switch checked={config.email_enabled} onCheckedChange={v => set('email_enabled', v)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 mb-1 block">Correo remitente</Label><Input value={config.email_from} onChange={e => set('email_from', e.target.value)} placeholder="cobranza@empresa.mx" /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Nombre remitente</Label><Input value={config.email_from_name} onChange={e => set('email_from_name', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 mb-1 block">Reply-to (opcional)</Label><Input value={config.email_reply_to} onChange={e => set('email_reply_to', e.target.value)} placeholder="responder@empresa.mx" /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Asunto por defecto</Label><Input value={config.email_default_subject} onChange={e => set('email_default_subject', e.target.value)} /></div>
          </div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Firma de correo</Label><Textarea value={config.email_signature} onChange={e => set('email_signature', e.target.value)} rows={3} className="text-sm" /></div>
        </CardContent>
      </Card>

      {/* ── WHATSAPP ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-emerald-500" /> WhatsApp</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Envío por WhatsApp habilitado</Label>
            <Switch checked={config.whatsapp_enabled} onCheckedChange={v => set('whatsapp_enabled', v)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 mb-1 block">Nombre remitente</Label><Input value={config.whatsapp_sender_name} onChange={e => set('whatsapp_sender_name', e.target.value)} /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Número remitente</Label><Input value={config.whatsapp_sender_number} onChange={e => set('whatsapp_sender_number', e.target.value)} placeholder="+52 1 XXX XXX XXXX" /></div>
          </div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Proveedor</Label>
            <Select value={config.whatsapp_provider} onValueChange={v => set('whatsapp_provider', v)}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="mock">Mock (sin envío real)</SelectItem><SelectItem value="twilio">Twilio</SelectItem><SelectItem value="meta">Meta Business API</SelectItem></SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── REGLAS ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> Reglas de Recordatorio</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 mb-1 block">Días antes del vencimiento</Label><Input value={(config.reminder_days_before || []).join(', ')} onChange={e => set('reminder_days_before', e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n)))} placeholder="7, 3" /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Días después del vencimiento</Label><Input value={(config.reminder_days_after || []).join(', ')} onChange={e => set('reminder_days_after', e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n)))} placeholder="1, 5, 15" /></div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Recordatorios para finiquitos</Label>
            <Switch checked={config.reminder_finiquitos} onCheckedChange={v => set('reminder_finiquitos', v)} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Requerir aprobación antes de enviar</Label>
            <Switch checked={config.reminder_require_approval} onCheckedChange={v => set('reminder_require_approval', v)} />
          </div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Canal por defecto</Label>
            <Select value={config.reminder_default_channel} onValueChange={v => set('reminder_default_channel', v)}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="email">Correo</SelectItem></SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── IA ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#F05A28]" /> IA de Cobranza</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div><Label className="text-xs text-slate-500 mb-1 block">Tono por defecto</Label>
            <Select value={config.ai_default_tone} onValueChange={v => set('ai_default_tone', v)}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cordial">Cordial</SelectItem><SelectItem value="formal">Formal</SelectItem><SelectItem value="firme">Firme</SelectItem><SelectItem value="urgente">Urgente</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 mb-1 block">Longitud máx. email (caracteres)</Label><Input type="number" value={config.ai_max_length_email} onChange={e => set('ai_max_length_email', parseInt(e.target.value) || 500)} /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Longitud máx. WhatsApp (caracteres)</Label><Input type="number" value={config.ai_max_length_whatsapp} onChange={e => set('ai_max_length_whatsapp', parseInt(e.target.value) || 300)} /></div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">IA genera borrador automático en cola</Label>
            <Switch checked={config.ai_auto_draft} onCheckedChange={v => set('ai_auto_draft', v)} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Siempre requerir revisión humana</Label>
            <Switch checked={config.ai_require_human_review} onCheckedChange={v => set('ai_require_human_review', v)} />
          </div>
        </CardContent>
      </Card>

      {/* ── PLANTILLAS ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" /> Plantillas Base</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-400">Variables disponibles: {'{client_name}'}, {'{amount}'}, {'{date}'}, {'{deed_date}'}</p>
          <div><Label className="text-xs text-slate-500 mb-1 block">Pago próximo a vencer</Label><Textarea value={config.template_upcoming} onChange={e => set('template_upcoming', e.target.value)} rows={2} className="text-sm" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Pago vencido</Label><Textarea value={config.template_overdue} onChange={e => set('template_overdue', e.target.value)} rows={2} className="text-sm" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Finiquito pendiente</Label><Textarea value={config.template_finiquito} onChange={e => set('template_finiquito', e.target.value)} rows={2} className="text-sm" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Seguimiento cordial</Label><Textarea value={config.template_cordial} onChange={e => set('template_cordial', e.target.value)} rows={2} className="text-sm" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Seguimiento firme</Label><Textarea value={config.template_firme} onChange={e => set('template_firme', e.target.value)} rows={2} className="text-sm" /></div>
        </CardContent>
      </Card>

      {/* Save button at bottom too */}
      <div className="flex justify-end pb-4">
        <Button onClick={handleSave} disabled={saving} size="lg">
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </div>
    </div>
  );
}
